import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  CheckCircleOutline,
  Save as SaveIcon,
  WarningRounded,
  LockOpen as LockOpenIcon,
  Audiotrack as AudioIcon,
  Movie as MovieIcon,
  PictureAsPdf as PdfIcon,
  Description as DocxIcon,
  Code as JsonIcon,
} from "@material-ui/icons";
import { useTranslation } from "../../hooks/useTranslation";
import { useAuth } from "../../context/ApiKeyContext";
import { isVideoFile, isAudioFile } from "../MediaPlayer";
import { formatDurationWords } from "../../utils/transcriptionFormat";
import {
  downloadTranscriptionAsJson,
  downloadTranscriptionAsDocx,
  downloadTranscriptionAsPdf,
  saveBlobAsFile,
} from "../../utils/transcriptionDownload";
import {
  downloadTranscriptionPdf,
  downloadTranscriptionDocx,
  downloadTranscriptionJson,
  isAbortError,
} from "../../utils/transcriptionApi";
import ConfirmDialog from "../ConfirmDialog";

/**
 * Helper local: monta um nome de arquivo "salvador" para o download
 * quando o backend não devolve `Content-Disposition` (ou quando caímos
 * no fallback local de DOCX-via-HTML, que usa `.doc`). Mantemos a
 * lógica isolada aqui — não vale exportar do `transcriptionDownload`
 * porque o mapeamento de extensão (`docx` real do backend × `doc`
 * do fallback) é específico desta camada.
 */
function buildLocalDownloadName(baseName, format) {
  const safe = String(baseName || "transcricao")
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\.[a-z0-9]{1,4}$/i, "")
    .trim() || "transcricao";
  /* No backend o DOCX é OOXML real (`.docx`); o fallback local gera
     Word-HTML (`.doc`). Aqui só nomeamos o arquivo recebido pelo
     backend — o fallback local define seu próprio nome. */
  const ext = format === "docx" ? "docx" : format;
  return `${safe}.${ext}`;
}

/**
 * Barra de status da transcrição (área superior do painel).
 *
 * Layout em 3 colunas:
 *   1. Ícone do tipo de mídia (audio/video) — coluna estreita.
 *   2. Bloco principal (preenche o espaço): nome do arquivo em destaque,
 *      duração + data de transcrição, tag de status (Em revisão / Aprovada
 *      / Somente leitura). Quando o status é "aprovada" no modo APPROVED_LOCKED,
 *      a tag inclui um botão "Voltar para edição" (sublinhado, sem bordas)
 *      que dispara `onCancelApproval`. Em READ_ONLY o botão não aparece.
 *   3. Ações alinhadas à direita:
 *      - effectiveMode "EDITING": botão "Salvar" (quando dirty) e "Aprovar".
 *      - effectiveMode "APPROVED_LOCKED" / "READ_ONLY": botões para baixar
 *        PDF / DOCX / JSON da transcrição revisada (a aprovação é a "versão
 *        oficial" e pode ser baixada). O download é gerado pelo backend
 *        (endpoints `GET .../download/{pdf|docx|json}`); em caso de
 *        erro 5xx/rede caímos para a geração local (compatibilidade
 *        com o comportamento anterior).
 *
 * APROVAÇÃO E NOME DO APROVADOR
 * -----------------------------
 * "Aprovada" é determinado pelo `effectiveMode` (calculado pelo painel a
 * partir de `approval_timestamp`) — não dependemos mais do nome do
 * aprovador para esta decisão. O nome (`approverUsername`) é apenas
 * cosmético: quando vem do backend, exibimos "Aprovada por <nome> em
 * <data>"; quando ausente, fazemos fallback para o nome do usuário
 * logado se ele mesmo for o aprovador (`approvedById === user.id`); senão
 * exibimos só "Aprovada em <data>".
 *
 * Props:
 *   - `effectiveMode`      "EDITING" | "APPROVED_LOCKED" | "READ_ONLY"
 *   - `jobId` / `transcriptionId` necessários para chamar os endpoints
 *                          de download do backend. Quando ausentes, os
 *                          handlers caem direto para a geração local.
 *   - `filename`           nome do arquivo (origina o ícone audio/video)
 *   - `transcription`      conteúdo atual (usado pelos downloads de fallback)
 *   - `mediaDuration`      duração total em segundos (recebida do player)
 *   - `approverUsername`   nome do aprovador (vindo do backend; pode ser null)
 *   - `approvedById`       user_id do aprovador (vindo do backend; usado
 *                          para fallback de nome quando aprovador = você)
 *   - `approvalTimestamp`  ISO string (data da aprovação)
 *   - `lastSavedTimestamp` ISO string (último save)
 *   - `transcribedAt`      ISO string da geração inicial (opcional). Hoje
 *                          o backend não devolve este campo separado, então
 *                          caímos para `lastSavedTimestamp` ou `approvalTimestamp`.
 *   - `hasUnsavedChanges`  boolean
 *   - `saving` / `approving` / `cancelingApproval` flags de loading
 *   - `onSave` / `onApprove` / `onCancelApproval`  handlers (o último só
 *                          é chamado em APPROVED_LOCKED — em READ_ONLY o
 *                          painel nem repassa o handler)
 */
function TranscriptionStatusBar({
  effectiveMode = "EDITING",
  jobId,
  transcriptionId,
  filename,
  transcription,
  mediaDuration,
  approverUsername,
  approvedById,
  approvalTimestamp,
  lastSavedTimestamp,
  transcribedAt,
  hasUnsavedChanges,
  saving = false,
  approving = false,
  cancelingApproval = false,
  onSave,
  onApprove,
  onCancelApproval,
}) {
  const { t, formatDateTime } = useTranslation("common");
  const { user, token } = useAuth();
  /* Controla a abertura do popup de confirmação de aprovação. Como o
     endpoint de aprovação no backend recebe o conteúdo da transcrição
     e atualiza a versão salva, o clique em "Aprovar" funciona como
     "salvar + aprovar" em uma única operação — por isso permitimos o
     clique mesmo quando há alterações pendentes (`hasUnsavedChanges`).
     A confirmação alerta o usuário e ajusta a mensagem quando há
     edições não salvas. */
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  /* Formato do download em curso (`"pdf" | "docx" | "json" | null`).
     Usado para mostrar spinner no botão correspondente e desabilitar
     os outros enquanto a chamada ao backend está em curso. */
  const [downloadingFormat, setDownloadingFormat] = useState(null);
  /* Última mensagem de erro de download (4xx propagado pelo backend).
     Renderizada como tooltip discreto no grupo de botões. 5xx e erros
     de rede são silenciados — caem para o fallback local. */
  const [downloadError, setDownloadError] = useState(null);

  const isEditing = effectiveMode === "EDITING";
  const isApproved = effectiveMode === "APPROVED_LOCKED";
  const isLocked = !isEditing; // APPROVED_LOCKED ou READ_ONLY → sem botões de salvar/aprovar
  const canRevertApproval =
    effectiveMode === "APPROVED_LOCKED" && Boolean(onCancelApproval);

  /* Resolve o "nome do aprovador" para exibição. Preferimos o que veio
     do backend (`approverUsername`); caímos para o nome do usuário logado
     quando ele mesmo aprovou (cobre o gap até o backend devolver o campo);
     caso nenhum dos dois esteja disponível, o nome é null e exibimos só a
     data de aprovação. NOTA: o contexto Auth chama o id de `user_id`
     (vem do backend de login), não `id`. */
  const loggedUserId = user?.user_id ?? user?.id ?? null;
  const resolvedApproverName =
    approverUsername ||
    (approvedById != null &&
    loggedUserId != null &&
    approvedById === loggedUserId
      ? user?.name || user?.email || null
      : null);

  /* Data "transcrita em": o backend ainda não devolve a data exata da
     geração da transcrição, portanto usamos a melhor aproximação
     disponível (timestamp explícito > último save > aprovação). */
  const transcribedWhen =
    transcribedAt || lastSavedTimestamp || approvalTimestamp || null;

  const MediaIcon = filename
    ? isVideoFile(filename)
      ? MovieIcon
      : isAudioFile(filename)
        ? AudioIcon
        : AudioIcon
    : AudioIcon;

  /* Metadata único usado pelos downloads de FALLBACK LOCAL (PDF/DOCX/JSON).
     Usamos o nome JÁ RESOLVIDO (`resolvedApproverName`) em vez do
     `approverUsername` puro: assim o documento exportado também aproveita
     o fallback "user logado é o aprovador" enquanto o backend não devolve
     o nome. Os downloads via backend NÃO usam esse metadata — o backend
     monta o documento a partir do banco. */
  const downloadMetadata = {
    approved_by_username: resolvedApproverName,
    approval_timestamp: approvalTimestamp,
    last_saved_timestamp: lastSavedTimestamp,
  };

  /* Handler genérico de download via backend com fallback local. Tenta
     o endpoint do backend; em caso de erro 4xx (ex.: sem permissão),
     mostra a mensagem ao usuário e PARA — esses erros são acionáveis. Em
     caso de 5xx ou falha de rede, faz fallback transparente para a
     geração local (compatibilidade com o comportamento anterior do
     painel) e registra um aviso no console. Sempre limpa o estado de
     loading no `finally`. */
  const runDownload = async (format, backendCall, localFallback) => {
    setDownloadError(null);
    setDownloadingFormat(format);
    try {
      if (token && jobId && transcriptionId) {
        try {
          const { blob, filename: serverFilename } = await backendCall();
          /* Backend dita o nome (Content-Disposition); se vier vazio
             caímos para um nome derivado do arquivo de mídia + extensão. */
          const safeName =
            serverFilename ||
            buildLocalDownloadName(filename || "transcricao", format);
          saveBlobAsFile(blob, safeName);
          return;
        } catch (err) {
          if (isAbortError(err)) return;
          /* Erro acionável (4xx/422): mostra ao usuário e não cai no
             fallback — caso contrário esconderíamos o problema. */
          const status = err?.status;
          if (typeof status === "number" && status >= 400 && status < 500) {
            setDownloadError(err?.message || t("transcription.status.downloadError"));
            return;
          }
          /* 5xx ou rede: cai para fallback local sem incomodar o usuário,
             mas deixa um trace para diagnóstico. */
          // eslint-disable-next-line no-console
          console.warn(
            `[TranscriptionStatusBar] backend ${format} download failed; falling back to local generator.`,
            err,
          );
        }
      }
      localFallback();
    } finally {
      setDownloadingFormat(null);
    }
  };

  const handleDownloadPdf = () =>
    runDownload(
      "pdf",
      () =>
        downloadTranscriptionPdf(token, jobId, transcriptionId, {
          version: "approved",
        }),
      () =>
        downloadTranscriptionAsPdf({
          transcription,
          filename,
          metadata: downloadMetadata,
        }),
    );
  const handleDownloadDocx = () =>
    runDownload(
      "docx",
      () =>
        downloadTranscriptionDocx(token, jobId, transcriptionId, {
          version: "approved",
        }),
      () =>
        downloadTranscriptionAsDocx({
          transcription,
          filename,
          metadata: downloadMetadata,
        }),
    );
  const handleDownloadJson = () =>
    runDownload(
      "json",
      () =>
        downloadTranscriptionJson(token, jobId, transcriptionId, {
          version: "approved",
        }),
      () =>
        downloadTranscriptionAsJson({
          transcription,
          filename,
          metadata: downloadMetadata,
        }),
    );

  /* Tag de status. APPROVED_LOCKED inclui o botão "Voltar para edição"
     (sublinhado, sem bordas) para destravar a UI; READ_ONLY não — apenas
     mostra a tag. EDITING mostra "Em revisão". */
  const renderStatusTag = () => {
    if (effectiveMode === "READ_ONLY") {
      return (
        <span className="transcription-status__tag transcription-status__tag--readonly">
          {t("transcription.status.tag.readonly")}
        </span>
      );
    }
    if (isApproved) {
      return (
        <span className="transcription-status__tag transcription-status__tag--approved">
          <CheckCircleOutline
            fontSize="small"
            className="transcription-status__tag-icon"
          />
          {t("transcription.status.tag.approved")}
          {canRevertApproval ? (
            <Tooltip title={t("transcription.status.unlockTooltip")}>
              <button
                type="button"
                className="transcription-status__tag-unlock"
                onClick={onCancelApproval}
                disabled={cancelingApproval || saving || approving}
              >
                {cancelingApproval ? (
                  <CircularProgress size={10} />
                ) : (
                  <LockOpenIcon
                    style={{ fontSize: "0.85rem", marginRight: 3 }}
                  />
                )}
                {t("transcription.status.unlockToEdit")}
              </button>
            </Tooltip>
          ) : null}
        </span>
      );
    }
    return (
      <span className="transcription-status__tag transcription-status__tag--in-review">
        {t("transcription.status.tag.in_review")}
      </span>
    );
  };

  return (
    <Box className="transcription-status-bar">
      {/* Coluna 1: ícone do tipo de mídia */}
      <Box className="transcription-status__media-icon">
        <MediaIcon />
      </Box>

      {/* Coluna 2: nome + duração + transcribedAt + tag */}
      <Box className="transcription-status__info">
        <Typography
          variant="subtitle1"
          className="transcription-status__filename"
          title={filename || ""}
        >
          {filename || t("untitled")}
        </Typography>
        <Box className="transcription-status__sub">
          {Number.isFinite(mediaDuration) && mediaDuration > 0 ? (
            <span className="transcription-status__duration">
              {t("transcription.status.duration", {
                value: formatDurationWords(mediaDuration, {
                  hour: t("transcription.status.durationParts.hour"),
                  hours: t("transcription.status.durationParts.hours"),
                  minute: t("transcription.status.durationParts.minute"),
                  minutes: t("transcription.status.durationParts.minutes"),
                  second: t("transcription.status.durationParts.second"),
                  seconds: t("transcription.status.durationParts.seconds"),
                  conjunction: t(
                    "transcription.status.durationParts.conjunction",
                  ),
                }),
              })}
            </span>
          ) : null}
          {transcribedWhen ? (
            <span className="transcription-status__transcribed-at">
              {Number.isFinite(mediaDuration) && mediaDuration > 0
                ? " — "
                : ""}
              {t("transcription.status.transcribedAt", {
                when: formatDateTime(transcribedWhen),
              })}
            </span>
          ) : null}
        </Box>
        <Box className="transcription-status__tag-row">
          {renderStatusTag()}
          {isApproved && approvalTimestamp ? (
            <Typography
              variant="caption"
              className="transcription-status__approved-meta"
            >
              {resolvedApproverName
                ? t("transcription.status.approvedByShort", {
                    name: resolvedApproverName,
                    when: formatDateTime(approvalTimestamp),
                  })
                : t("transcription.status.approvedAtShort", {
                    when: formatDateTime(approvalTimestamp),
                  })}
            </Typography>
          ) : null}
          {isEditing && hasUnsavedChanges ? (
            <Tooltip title={t("transcription.status.unsavedTooltip")}>
              <Box className="transcription-status__unsaved">
                <WarningRounded fontSize="small" />
                <Typography variant="caption">
                  {t("transcription.status.unsavedShort")}
                </Typography>
              </Box>
            </Tooltip>
          ) : isEditing && lastSavedTimestamp ? (
            <Typography
              variant="caption"
              className="transcription-status__last-saved"
            >
              {t("transcription.status.lastSavedAt", {
                when: formatDateTime(lastSavedTimestamp),
              })}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {/* Coluna 3: ações alinhadas à direita. Em REVISAO mostramos os
          botões de salvar / aprovar; quando aprovada (ou em
          SOMENTE_LEITURA) mostramos os botões de download. As duas
          variantes podem coexistir caso o usuário acabou de "voltar para
          edição" (status volta para in_review). */}
      <Box className="transcription-status__actions">
        {isLocked ? (
          <Box
            className="transcription-status__downloads"
            role="group"
            aria-label={t("transcription.status.downloadGroupTooltip")}
          >
            {["pdf", "docx", "json"].map((fmt) => {
              const isDownloading = downloadingFormat === fmt;
              const otherDownloading =
                downloadingFormat !== null && !isDownloading;
              const Icon =
                fmt === "pdf" ? PdfIcon : fmt === "docx" ? DocxIcon : JsonIcon;
              const handler =
                fmt === "pdf"
                  ? handleDownloadPdf
                  : fmt === "docx"
                    ? handleDownloadDocx
                    : handleDownloadJson;
              const baseTooltipKey =
                fmt === "pdf"
                  ? "transcription.status.downloadPdf"
                  : fmt === "docx"
                    ? "transcription.status.downloadDocx"
                    : "transcription.status.downloadJson";
              /* Erro acionável (4xx do backend) é exibido no tooltip
                 do botão clicado para deixar claro o que aconteceu;
                 sem botão "atual", o tooltip do PDF (primeiro botão)
                 carrega a mensagem para que o usuário a veja ao
                 hover. Erros de 5xx/rede são silenciados (caem no
                 fallback local). */
              const tooltipTitle = downloadError
                ? downloadError
                : t(baseTooltipKey);
              return (
                <Tooltip key={fmt} title={tooltipTitle}>
                  <span>
                    <IconButton
                      size="small"
                      className="transcription-status__download-btn"
                      onClick={handler}
                      disabled={!transcription || otherDownloading}
                      aria-label={t(baseTooltipKey)}
                    >
                      {isDownloading ? (
                        <CircularProgress size={14} />
                      ) : (
                        <Icon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              );
            })}
          </Box>
        ) : null}

        {isEditing && hasUnsavedChanges && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            disabled={saving || approving || cancelingApproval}
            startIcon={
              saving ? (
                <CircularProgress size={14} />
              ) : (
                <SaveIcon fontSize="small" />
              )
            }
            onClick={onSave}
          >
            {saving
              ? t("transcription.status.savingShort")
              : t("transcription.status.saveChanges")}
          </Button>
        )}
        {isEditing && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={approving || saving}
            startIcon={
              approving ? (
                <CircularProgress size={14} style={{ color: "inherit" }} />
              ) : (
                <CheckCircleOutline fontSize="small" />
              )
            }
            onClick={() => setConfirmApproveOpen(true)}
          >
            {approving
              ? t("transcription.status.approvingShort")
              : t("transcription.status.approve")}
          </Button>
        )}
      </Box>

      {/* Popup de confirmação de aprovação. A mensagem muda quando há
          alterações não salvas para deixar claro ao usuário que a ação
          também irá persistir essas mudanças. */}
      <ConfirmDialog
        open={confirmApproveOpen}
        title={t("transcription.status.approveConfirmTitle")}
        message={
          hasUnsavedChanges
            ? t("transcription.status.approveConfirmTextDirty")
            : t("transcription.status.approveConfirmText")
        }
        confirmLabel={t("transcription.status.approve")}
        confirmIcon={<CheckCircleOutline fontSize="small" />}
        cancelLabel={t("transcription.status.approveConfirmCancel")}
        onClose={() => setConfirmApproveOpen(false)}
        onConfirm={() => {
          setConfirmApproveOpen(false);
          onApprove?.();
        }}
      />
    </Box>
  );
}

/* `React.memo`: a status bar só muda quando flags/datas/aprovação mudam,
   o que é raro. Sem memoização ela re-renderizava junto com o painel a
   cada transição de frase do player. */
export default React.memo(TranscriptionStatusBar);
